import styled from 'styled-components';
import React, { SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push as Push } from 'redux-first-history';
import {
    ContentLoader,
    CustomTableRowsProps,
    DotsIcon,
    palette,
    SimplePopover,
    TableRowNameLink,
    TemplatePreview,
    Typography,
} from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { Campaign, CampaignStatus, CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import { map, truncate } from 'lodash-es';

import { DropdownOptions } from '@/store/automations/types';
import { CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { getCampaignCreatorType } from '@/services/tools/getCampaignCreatorType';
import {
    CopyCampaignsByNameStart,
    DeleteCampaigns,
    getCampaignsStore,
    PreviewCampaign,
} from '@/store/campaigns/actions';
import { getApiPath } from '@/services/Secuirty';
import { ShowDialog } from '@/store/dialog/actions';
import { getConfigStore } from '@/store/config/types';
import { StatusBadge } from '@/assets/StatusBadge';

const TableItemButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const getRowPopoverOptions = (t: any): Record<string, string>[] => [
    {
        label: t('edit'),
        value: DropdownOptions.Edit,
    },
    {
        label: t('copycampaign'),
        value: DropdownOptions.CopyCampaign,
    },
    {
        label: t('delete'),
        value: DropdownOptions.Delete,
        color: palette.red[6],
    },
];

interface useTableRowsInterface {
    campaignTypeFilter?: CampaignTypeFilter[];
}

export const useTableRows = ({ campaignTypeFilter = [] }: useTableRowsInterface): CustomTableRowsProps[] => {
    const { t } = useTranslation();
    const campaigns = useSelector(getCampaignsStore);
    const config = useSelector(getConfigStore);
    const { pagination } = campaigns;
    const dispatch = useDispatch();

    const edit = (campaign: Campaign): void => {
        if (config.isStandalone) {
            const campaignCreatorType = getCampaignCreatorType(campaign.Type);
            dispatch(
                Push(
                    `${CampaignsPaths.CampaignCreatorName.replace(pathsParams.type, campaignCreatorType).replace(
                        pathsParams.name,
                        safelyEncodeBase64Url(campaign.Name)
                    )}`
                )
            );
        } else {
            window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
                ':name',
                safelyEncodeBase64Url(campaign.Name)
            )}`;
        }
    };

    const copyCampaign = (name: string) => {
        dispatch(
            ShowDialog({
                title: t('copycampaign'),
                body: `${t('copycampaignconfirmation')} "${name}"?`,
                confirmLabel: t('copy'),
                cancelLabel: t('cancel'),
                onClickConfirm: () => dispatch(CopyCampaignsByNameStart(name)),
            })
        );
    };

    const rowDropdownOptionsHandler = (
        _ev: SyntheticEvent<HTMLElement>,
        option: { label: string; value: DropdownOptions },
        campaign?: Campaign
    ): void => {
        switch (option.value) {
            case DropdownOptions.Edit:
                edit(campaign);
                break;
            case DropdownOptions.CopyCampaign:
                copyCampaign(campaign.Name);
                break;
            case DropdownOptions.Delete:
                dispatch(DeleteCampaigns([campaign.Name], campaignTypeFilter));
            default:
                break;
        }
    };

    if (campaigns?.loading) {
        const perPage = pagination?.perPage || 5;
        const rows: CustomTableRowsProps[] = [];

        for (let index = 0; index < perPage; index++) {
            rows?.push({
                preview: <ContentLoader mode="row" height={70} width={70} />,
                campaignName: <ContentLoader mode="row" height={20} />,
                status: [
                    <ContentLoader mode="row" height={20} key="skeleton" />,
                    <ContentLoader mode="row" height={20} key="skeleton" />,
                ],
                rowID: index.toString(),
            });
        }

        return rows;
    }

    return map(campaigns?.collection, (singleCampaign: Campaign, index): CustomTableRowsProps => {
        const campaignTemplatesThumbnail = singleCampaign?.CampaignTemplates[0]?.MediumScreenshot
            ? `${getApiPath()}/userfile${singleCampaign.CampaignTemplates[0].MediumScreenshot}`
            : '';

        return {
            preview: (
                <div
                    onClick={(ev): void => {
                        ev.stopPropagation();
                    }}
                >
                    <TemplatePreview
                        split={singleCampaign.CampaignTemplates.length > 1}
                        screenshot={campaignTemplatesThumbnail}
                        onClick={(): void => {
                            dispatch(PreviewCampaign(singleCampaign));
                        }}
                    />
                </div>
            ),
            campaignName: (
                <div>
                    <Typography variant={'caption'} color={'#280424'} className={'campaign-name'}>
                        <TableRowNameLink
                            onClick={(): void => {
                                edit(singleCampaign);
                            }}
                        >
                            {truncate(singleCampaign.Name, { length: 100, omission: '...' })}
                        </TableRowNameLink>
                    </Typography>
                </div>
            ),
            status: (
                <TableItemButtonsWrapper>
                    <StatusBadge key={`status-${index}`} value={singleCampaign.Status} variant={CampaignStatus.Draft} />
                    <div
                        onClick={(ev): void => {
                            ev.stopPropagation();
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <SimplePopover
                            type="icon"
                            options={getRowPopoverOptions(t)}
                            enableCheckbox={false}
                            onItemClick={(ev, option: { label: string; value: DropdownOptions }): void => {
                                rowDropdownOptionsHandler(ev, option, singleCampaign);
                            }}
                        >
                            <DotsIcon />
                        </SimplePopover>
                    </div>
                </TableItemButtonsWrapper>
            ),
            rowID: singleCampaign.Name,
        };
    });
};
